import c from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import tw from 'twin.macro';
import Error from '../../../components/Error';
import { ROUTES } from '../../../routes/routes';
import { CustomerPortal } from '../../../types/customerPortal';
import { GatedContentConfiguration, GatedSheetsConfiguration } from '../../../types/gatedContent';
import { PricingPage } from '../../../types/pricingPage';
import { withCustomerPortalIdParam, withPageIdParam } from './utils';

function isPricingPage(item: Props['item']): item is PricingPage {
    return !!(item as PricingPage).status;
}

function isCustomerPortal(item: Props['item']): item is CustomerPortal {
    return !!(item as CustomerPortal).business_profile;
}

const Wrapper = tw.div`sticky w-full bottom-0 right-0 h-14 rounded`;
const FlexWrapper = tw.div`flex flex-row p-2 shadow rounded bg-white`;

export const PRODUCTS_PARAM = 'products';

interface Step {
    href: ROUTES;
    label: string;
}

const STEPS_PRICING_PAGES: Step[] = [
    {
        href: ROUTES.PAGES_NEW,
        label: 'Products',
    },
    { href: ROUTES.PAGES_NEW_FLOW, label: 'Payment' },
    { href: ROUTES.PAGES_NEW_FEATURES, label: 'Configure' },
    { href: ROUTES.PAGES_NEW_DESIGN, label: 'Design' },
    { href: ROUTES.PAGES_NEW_SNIPPET, label: 'Snippet' },
];

const STEPS_CUSTOMER_PORTAL: Step[] = [
    {
        href: ROUTES.CUSTOMER_PORTALS_NEW,
        label: 'Choose Pricing',
    },
    { href: ROUTES.CUSTOMER_PORTALS_CONFIG, label: 'Configure' },
    { href: ROUTES.CUSTOMER_PORTALS_SNIPPET, label: 'Snippet' },
];

const replaceId = (url: string, id: string) => url.replace(':id', id);

interface Link {
    href: ROUTES;
    disabled: boolean;
    label?: string;
}
interface Props {
    item: PricingPage | CustomerPortal | GatedContentConfiguration | GatedSheetsConfiguration;
    error?: string;
    next?: Link;
    prev?: Link;
    size?: 'SMALL' | 'LARGE';
}

const Footer = ({ item, next, prev, error, size = 'SMALL' }: Props) => {
    const { pathname } = useLocation();
    const history = useHistory();
    const pushStep = (href: ROUTES) =>
        history.push(
            isPricingPage(item)
                ? withPageIdParam(href, item.id)
                : isCustomerPortal(item)
                ? withCustomerPortalIdParam(href, item)
                : replaceId(href, item.id)
        );
    const handleNextStep = () => {
        if (next) {
            pushStep(next.href);
        }
    };
    const handlePrevStep = () => {
        if (prev) {
            pushStep(prev.href);
        }
    };

    const steps = isPricingPage(item) ? STEPS_PRICING_PAGES : isCustomerPortal(item) ? STEPS_CUSTOMER_PORTAL : [];

    const isCurrentStep = (step: Step) => pathname === step.href;
    const canNavigate = isPricingPage(item) ? item.status !== 'DRAFT' : steps.length > 2;

    return (
        <Wrapper>
            <FlexWrapper>
                <ol
                    className={c(
                        'mx-auto flex divide-x divide-gray-100 overflow-y-hidden overflow-x-scroll rounded-lg border border-gray-100 text-gray-500 shadow',
                        { 'text-xs sm:text-sm': size === 'SMALL' }
                    )}
                >
                    {prev && !canNavigate && (
                        <li className="flex items-center justify-center bg-gray-500 hover:bg-gray-700 text-white">
                            <button
                                onClick={handlePrevStep}
                                className={c({ 'px-4': !!prev.label, 'px-4 py-2': size === 'LARGE' })}
                                disabled={prev.disabled}
                            >
                                {prev.label ? (
                                    prev.label
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-6 h-6"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M15.75 19.5L8.25 12l7.5-7.5"
                                        />
                                    </svg>
                                )}
                            </button>
                        </li>
                    )}
                    {steps.map((step) => (
                        <li
                            key={step.href}
                            className={c('flex items-center justify-center px-2 md:px-4 md:py-2 w-20 xl:w-auto', {
                                'bg-gray-100': isCurrentStep(step),
                                'text-gray-300': !canNavigate,
                            })}
                        >
                            {canNavigate && (
                                <button
                                    className={c('leading-none', { 'px-4 py-2': size === 'LARGE' })}
                                    onClick={() => pushStep(step.href)}
                                >
                                    <strong className="block font-medium"> {step.label} </strong>
                                </button>
                            )}
                            {!canNavigate && <strong className="block font-medium"> {step.label} </strong>}
                        </li>
                    ))}
                    {next && (
                        <li className="flex items-center justify-center bg-green-500 hover:bg-green-700 text-white">
                            <button
                                className={c({ 'px-4': !!next.label, 'px-4 py-2': size === 'LARGE' })}
                                onClick={handleNextStep}
                                disabled={next.disabled}
                                title={error}
                            >
                                <div className="flex items-center pl-2">
                                    <div>{next.label ? next.label : 'Next'}</div>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-5 h-5"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                        />
                                    </svg>
                                </div>
                            </button>
                        </li>
                    )}
                </ol>

                {error && (
                    <div className="ml-auto flex items-center pt-2 px-4 mr-auto">
                        <Error message={error} />
                    </div>
                )}
            </FlexWrapper>
        </Wrapper>
    );
};

export default Footer;
